import {
  EMPTY_ARRAY,
  formattedUTCDay,
  getAgGridColId,
  Icon,
  IconName,
  IndicatorBadge,
  prettyPriceProtection,
  Text,
  useDefaultColumns,
  useMarketsContext,
  type CareOrder,
  type Column,
  type ColumnDef,
  type Leaves,
  type SubAccountColumnParams,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import { CARE_ORDER_CUSTOM_COLUMN_IDS } from './careOrderColumnIds';
import { getCareOrderBlotterEntityStatusColumn } from './columns/CareOrderBlotterEntityStatusColumn';
import { getCareOrderExportMakerColumns } from './columns/careOrderExportMakerColumns';
import { getCareOrderProgressColumn } from './columns/careOrderProgressColumn';
import { legRowFadedCellClassRules } from './columns/legRowFadedCellClassRules';
import { getRowTypeColumn } from './columns/rowTypeColumn';
import { unitsTradedColumn } from './columns/unitsTradedColumn';
import { isCareOrderRow, isLegRow, isOrderRow, type CareOrderBlotterEntity } from './types';
import {
  useCareOrderBlotterActionColumn,
  type UseCareOrderBlotterActionColumnParams,
} from './useCareOrderBlotterActionColumn';

export type UseCareOrderColumns = {
  defaultColumns?: (keyof CareOrder | Partial<Column>)[];
} & UseCareOrderBlotterActionColumnParams;

export const useCareOrderColumns = ({
  defaultColumns = EMPTY_ARRAY,
  onSendRFQ,
  onShowRFQ,
  onShowTimeline,
}: UseCareOrderColumns) => {
  const {
    enableCareOrderExternalIDField,
    enableCareOrderNetting,
    enableCareOrderSettleDateField,
    supportAdjustedSettlementDate,
    careOrderExportMakerList,
  } = useFeatureFlag();
  const { marketDisplayNameByName } = useMarketsContext();
  const actionColumn = useCareOrderBlotterActionColumn({ onSendRFQ, onShowRFQ, onShowTimeline });

  const columnDefinitions = useMemo(() => {
    const makerExportColumns = getCareOrderExportMakerColumns({
      marketNames: careOrderExportMakerList,
      marketDisplayNameByName,
    });

    return new Map(
      compact([
        enableCareOrderNetting && {
          ...getRowTypeColumn<CareOrderBlotterEntity>({
            id: CARE_ORDER_CUSTOM_COLUMN_IDS.Type,
            initialPinned: 'left',
            getRowType: data => data.RowType,
          }),
        },
        {
          type: 'date',
          field: 'SubmitTime',
          title: 'Submit Time',
          initialSort: '-',
          params: { milliseconds: true },
          initialPinned: 'left',
          initialWidth: 150,
          cellClassRules: legRowFadedCellClassRules,
        },
        {
          type: 'quoteSide',
          field: 'tradedSideOrSide',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.Side,
          title: 'Side',
          initialPinned: 'left',
          cellClassRules: legRowFadedCellClassRules,
        },
        {
          type: 'size',
          initialPinned: 'left',
          field: 'OrderQty',
          title: 'Order Qty',
          initialWidth: 100,
          params: {
            currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity>,
          },
        },
        {
          type: 'security',
          field: 'Symbol',
          title: 'Symbol',
          initialWidth: 160, // wider to also accomodate symbol with text "FUND:GBTC:2026-01-01"
          initialPinned: 'left',
        },
        getCareOrderProgressColumn(),
        {
          field: 'WorkingQty',
          initialWidth: 100,
          type: 'size',
          initialPinned: 'left',
          params: { currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity> },
        },
        {
          type: 'counterparty',
          field: 'Counterparty',
          title: 'Counterparty',
          initialWidth: 180, // this is often a longer-form text like "ABN Amro Clearing USA"
        },
        {
          type: 'text',
          field: 'initiatingFrom',
          title: 'Initiating From',
          initialWidth: 140, // this is often a shorter text like "Jane Street"
        },
        { field: 'User', type: 'user' },
        getCareOrderBlotterEntityStatusColumn({ initialPinned: 'left' }),
        { field: 'TransactTime', type: 'date' },
        {
          type: 'size',
          title: 'Remaining Qty',
          field: 'AvailableQty',
          initialPinned: 'left',
          params: {
            currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity>,
          },
        },
        {
          type: 'size',
          field: 'filledQty',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.CumQty, // <-- id staying as CumQty for consistent colid post field change
          title: 'Filled Qty',
          params: {
            currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity>,
          },
        },
        {
          field: 'filledAmt',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.CumAmt, // <-- id staying as CumAmt for consistent colid post field change
          title: 'Cum Amt',
          type: 'size',
          initialHide: true,
          params: { currencyField: 'AmountCurrency' satisfies Leaves<CareOrderBlotterEntity> },
        },
        {
          type: 'number',
          field: 'units',
          title: 'Units',
          description: 'Total Units specified on the Care Order',
          initialHide: true,
          initialWidth: 100,
        },
        unitsTradedColumn,
        {
          type: 'text',
          field: 'fund',
          initialHide: true,
        },
        {
          type: 'text',
          field: 'Group',
          title: 'Group',
          initialWidth: 120,
        },
        enableCareOrderExternalIDField && {
          type: 'text',
          field: 'ExternalGroup',
          title: 'External ID',
        },
        enableCareOrderSettleDateField && {
          type: 'date',
          field: 'SettleDate',
          title: 'Settlement Date',
          params: { customFormatter: formattedUTCDay },
        },
        enableCareOrderSettleDateField &&
          supportAdjustedSettlementDate && {
            type: 'custom',
            field: 'Annotations.SettleOffset',
            title: 'Settle Offset',
            params: { valueFormatter: ({ value }) => (value ? `T+${value}` : '') },
            initialWidth: 100,
          },
        {
          field: 'fixingIndices',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.FixingIndices,
          title: 'Fixing Index',
          type: 'fixingIndices',
        },
        { field: 'Text', type: 'text' },
        {
          type: 'custom',
          title: 'Route',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.Route,
          initialHide: true,
          params: {
            initialWidth: 55,
            valueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>) => {
              if (params.data == null || params.node?.id == null) {
                return null;
              }
              if (isCareOrderRow(params.data)) {
                return params.api.getRowNode(params.node.id)?.childrenAfterGroup?.length ?? 0;
              }
              return isOrderRow(params.data) ? 'Order' : 'RFQ';
            },
            cellRenderer: (params: ICellRendererParams<CareOrderBlotterEntity>) => {
              if (params.data == null || isLegRow(params.data)) {
                return null;
              }
              if (isCareOrderRow(params.data)) {
                return (
                  <IndicatorBadge>
                    <Icon icon={IconName.Share} />
                    <Text ml="spacing2">{params.value}</Text>
                  </IndicatorBadge>
                );
              }

              return params.value;
            },
          },
        },
        {
          type: 'text',
          field: 'Annotations.FIX.OrderOriginationTrader',
          title: 'Origination Trader',
          initialHide: true,
        },
        {
          type: 'side',
          field: 'TradedSide',
          title: 'Traded Side',
          initialHide: true,
          params: {
            fallbackField: 'Side',
          },
        },
        {
          type: 'strategy',
          field: 'Strategy',
          title: 'Strategy',
          initialHide: true,
        },
        {
          field: 'approvedMarkets',
          type: 'markets',
          title: 'Approved Markets',
          params: { emptyArrayText: 'All available' },
          initialHide: true,
        },
        { field: 'selectedMarkets', title: 'Selected Markets', type: 'markets', initialHide: true },
        { field: 'tradedMarkets', type: 'markets', title: 'Traded Markets' },
        { field: 'tradedMarketAccounts', type: 'marketAccounts', title: 'Traded Market Accounts' },
        {
          type: 'price',
          field: 'Price',
          title: 'Price',
          initialHide: true,
          params: {
            assetField: 'Symbol' satisfies Leaves<CareOrderBlotterEntity>,
          },
        },
        {
          field: 'PricingReference',
          type: 'price',
          title: 'Pricing Reference',
          initialHide: true,
          params: {
            isReferencePrice: true,
          },
        },
        {
          type: 'price',
          field: 'AvgPx',
          title: 'Filled Price (no fees)',
          initialHide: true,
          params: {
            assetField: 'Symbol' satisfies Leaves<CareOrderBlotterEntity>,
          },
        },
        {
          type: 'price',
          field: 'TradedAmt',
          title: 'Traded Amt',
          params: {
            assetField: 'Symbol' satisfies Leaves<CareOrderBlotterEntity>,
          },
          initialHide: true,
        },
        {
          type: 'text',
          field: 'Comments',
          title: 'Comments',
          initialHide: true,
        },
        {
          type: 'text',
          field: 'ExternalComments',
          title: 'External Comments',
          initialHide: true,
        },
        {
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.SubAccount,
          field: 'subAccounts',
          type: 'subAccount',
          title: 'Sub Account(s)',
          params: {
            allocationField: 'Allocation',
            allocationCurrencyField: 'Currency',
          } satisfies SubAccountColumnParams<CareOrderBlotterEntity>,
        },
        { field: 'RiskStatus', type: 'bitmap', initialHide: true },
        {
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.FilledNotional,
          type: 'filledNotional',
          initialHide: true,
          title: 'Filled Notional',
          params: { securityField: 'Symbol', showFeeIcon: 'never' },
        },

        {
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.OpenNotional,
          type: 'openNotional',
          initialHide: true,
          title: 'Open Notional',
          params: { securityField: 'Symbol', showFeeIcon: 'never' },
        },
        {
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.TotalNotional,
          type: 'totalNotional',
          initialHide: true,
          title: 'Total Notional',
          params: { securityField: 'Symbol', showFeeIcon: 'never' },
        },
        {
          type: 'size',
          initialHide: true,
          title: 'Net Notional',
          field: 'netNotional', // currently only exists on the QuoteRow
          params: { currencyField: 'AmountCurrency' satisfies keyof CareOrderBlotterEntity },
        },
        {
          type: 'size',
          initialHide: true,
          title: 'Fees',
          field: 'fees', // currently only exists on the QuoteRow
          params: { currencyField: 'AmountCurrency' satisfies keyof CareOrderBlotterEntity },
        },
        {
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.FilledCounterAmount,
          type: 'filledCounterAmount',
          initialHide: true,
          title: 'Filled Counter Amt',
          params: { currencyField: 'AmountCurrency', showFeeIcon: 'never' },
        },
        {
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.ProductType,
          type: 'productType',
          initialHide: true,
          title: 'Product',
          initialWidth: 100,
        },
        { id: CARE_ORDER_CUSTOM_COLUMN_IDS.BaseQty, type: 'cumBaseQty', initialHide: true, title: 'Filled Base Qty' },
        { field: 'endTime', type: 'date', initialHide: true },
        { field: 'LastTradeTime', type: 'date', initialHide: true },
        { field: 'startTime', type: 'date', initialHide: true },
        {
          field: 'Timestamp',
          type: 'date',
          title: 'Updated at',
          params: { milliseconds: true },
        },
        { field: 'OrdType', type: 'text', initialHide: true, title: 'Order Type', initialWidth: 100 },
        { field: 'ClOrdID', type: 'id', initialHide: true, initialWidth: 80 },
        {
          field: 'LeavesQty',
          type: 'size',
          initialHide: true,
          params: { currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity> },
          // TODO fhqvst This one is interesting - I'm not sure 'Open Qty' is correct
          title: 'Open Qty',
        },
        { field: 'TimeInForce', type: 'text', initialHide: true },
        { field: 'DecisionStatus', type: 'text', initialHide: true },
        { field: 'ExecID', type: 'text', initialHide: true },
        { field: 'Parameters', type: 'orderParameters', initialHide: true },
        {
          field: 'ExpectedFillPrice',
          type: 'price',
          initialHide: true,
          params: { assetField: 'Symbol' satisfies Leaves<CareOrderBlotterEntity>, showFeeIcon: 'never' },
        },
        {
          field: 'ExpectedFillQty',
          type: 'size',
          initialHide: true,
          params: { currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity> },
        },
        { field: 'ParentOrderID', type: 'text', initialHide: true },
        { field: 'Revision', type: 'text', initialHide: true, initialWidth: 70 },
        { field: 'OrigClOrdID', type: 'text', initialHide: true },
        { field: 'SessionID', type: 'text', initialHide: true },
        { field: 'Allocation', type: 'text', initialHide: true },
        { field: 'CxlRejReason', type: 'text', initialHide: true },
        { field: 'AggressorSide', type: 'side', initialHide: true },
        {
          field: 'allowedSlippageBPS',
          title: 'Allowed Slippage',
          type: 'number',
          initialHide: true,
          params: { currency: 'BPS' },
        },
        {
          type: 'custom',
          params: { valueFormatter: ({ value }) => prettyPriceProtection(value) },
          field: 'priceProtection',
          initialHide: true,
        },
        { field: 'ParentRFQID', type: 'id', initialHide: true, initialWidth: 80 },
        { field: 'QuoteID', type: 'id', initialHide: true, initialWidth: 80 },
        { field: 'RFQID', type: 'id', initialHide: true, initialWidth: 80 },
        { field: 'unifiedLiquidity', type: 'text', initialHide: true },
        { field: 'reduceOnly', type: 'text', initialHide: true },
        { field: 'reduceFirst', type: 'text', initialHide: true },
        {
          field: 'FixingDetails',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.FixingPrice,
          title: 'Fixing Price',
          type: 'fixingPrice',
        },
        {
          field: 'fixingDate',
          id: CARE_ORDER_CUSTOM_COLUMN_IDS.FixingDate,
          title: 'Fixing Date',
          type: 'dateOnly',
          initialHide: true,
        },
        {
          field: 'Currency',
          type: 'currency',
          initialHide: true,
        },
        {
          field: 'QuoteReqID',
          type: 'id',
          initialHide: true,
        },
        {
          field: 'QuoteRequestRejectReason',
          type: 'text',
          initialHide: true,
        },
        {
          field: 'AmountCurrency',
          type: 'currency',
          initialHide: true,
        },
        {
          field: 'FirstTransactTime',
          type: 'date',
          initialHide: true,
        },
        {
          field: 'EndTime',
          type: 'date',
          initialHide: true,
        },
        {
          field: 'TradedPx',
          type: 'price',
          params: { assetField: 'Symbol' satisfies Leaves<CareOrderBlotterEntity> },
          initialHide: true,
        },
        {
          field: 'TradedQty',
          type: 'size',
          params: { currencyField: 'Currency' satisfies Leaves<CareOrderBlotterEntity> },
          initialHide: true,
        },
        {
          field: 'TradedAmt',
          type: 'size',
          params: { currencyField: 'AmountCurrency' satisfies Leaves<CareOrderBlotterEntity> },
          initialHide: true,
        },
        {
          field: 'TradedSide',
          type: 'side',
          initialHide: true,
        },
        actionColumn,
        ...makerExportColumns,
      ] satisfies (ColumnDef<CareOrderBlotterEntity> | false)[]).map(c => [getAgGridColId(c), c])
    );
  }, [
    actionColumn,
    careOrderExportMakerList,
    enableCareOrderExternalIDField,
    enableCareOrderNetting,
    enableCareOrderSettleDateField,
    marketDisplayNameByName,
    supportAdjustedSettlementDate,
  ]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
